import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Database, Search, FolderKanban, ShieldCheck, BookMarked,
  ArrowRight, Check, ExternalLink, FlaskConical, Menu, X, KeyRound, Smartphone
} from "lucide-react";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import api, { fileUrl } from "@/lib/api";

const ICONS = [MessageSquare, Database, Search, FolderKanban, ShieldCheck, BookMarked];

export default function Landing() {
  const [c, setC] = useState({});
  const [plans, setPlans] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pricingVisible, setPricingVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data)).catch(() => {});
    api.get("/plans").then((r) => { setPlans(r.data.plans); setPricingVisible(r.data.pricing_visible !== false); }).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  const t = (k, fb = "") => (c[k] != null && c[k] !== "" ? c[k] : fb);
  const larsicomUrl = t("larsicom_website", "https://larsicomrdc.org/");
  const steps = t("positioning_steps", "Créer|Chercher|Comprendre|Structurer|Rédiger|Vérifier|Améliorer|Continuer").split("|").map((s) => s.trim()).filter(Boolean);
  const features = [1, 2, 3, 4, 5, 6].map((i, idx) => ({
    icon: ICONS[idx], title: t(`feature_${i}_title`), desc: t(`feature_${i}_desc`),
  })).filter((f) => f.title);

  return (
    <div className="min-h-screen bg-lisungi-bg text-ink">
      {/* HEADER compact 76px */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-navy border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 h-[76px] flex items-center justify-between">
          <Logo variant="light" />
          <div className="hidden md:flex items-center gap-7 text-sm text-white/80">
            <a href="#larsicom" className="hover:text-lisungi-yellow transition-colors" data-testid="nav-larsicom">{t("nav_larsicom", "Le LARSICOM")}</a>
            <a href="#features" className="hover:text-lisungi-yellow transition-colors" data-testid="nav-features">{t("nav_features", "Fonctionnalités")}</a>
            {pricingVisible && <a href="#pricing" className="hover:text-lisungi-yellow transition-colors" data-testid="nav-pricing">{t("nav_pricing", "Offres")}</a>}
            <Link to="/responsable" className="hover:text-lisungi-yellow transition-colors" data-testid="nav-responsible">{t("nav_responsible", "Usage responsable")}</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-login">{t("nav_login", "Se connecter")}</Link>
            <Link to="/login?mode=register">
              <Button className="bg-lisungi-yellow text-navy hover:bg-lisungi-yellow/90 rounded-full font-semibold" data-testid="nav-cta">{t("nav_cta", "Commencer")}</Button>
            </Link>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white p-1.5 rounded-lg hover:bg-white/10" data-testid="landing-menu-btn" aria-label="Menu">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-navy border-t border-white/10 px-5 py-4 space-y-3 text-sm text-white/85" data-testid="landing-mobile-menu">
            <a href="#larsicom" onClick={() => setMenuOpen(false)} className="block hover:text-lisungi-yellow">{t("nav_larsicom", "Le LARSICOM")}</a>
            <a href="#features" onClick={() => setMenuOpen(false)} className="block hover:text-lisungi-yellow">{t("nav_features", "Fonctionnalités")}</a>
            {pricingVisible && <a href="#pricing" onClick={() => setMenuOpen(false)} className="block hover:text-lisungi-yellow">{t("nav_pricing", "Offres")}</a>}
            <Link to="/responsable" onClick={() => setMenuOpen(false)} className="block hover:text-lisungi-yellow">{t("nav_responsible", "Usage responsable")}</Link>
            <Link to="/login" onClick={() => setMenuOpen(false)} className="block hover:text-lisungi-yellow border-t border-white/10 pt-3">{t("nav_login", "Se connecter")}</Link>
          </div>
        )}
      </nav>

      {/* HERO — sobre, académique, institutionnel */}
      <header className="pt-[76px] bg-navy text-white">
        <div className="max-w-7xl mx-auto px-5 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.22em] text-lisungi-yellow mb-5" data-testid="hero-lead">
              {t("hero_lead", "Assistant intelligent conçu par le LARSICOM")}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight" data-testid="hero-title">
              {t("hero_title", "Votre espace de recherche scientifique.")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {t("hero_subtitle")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/login?mode=register">
                <Button size="lg" className="bg-lisungi-yellow text-navy hover:bg-lisungi-yellow/90 rounded-md font-semibold px-7 h-12" data-testid="hero-cta">
                  {t("hero_cta", "Créer mon espace de recherche")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href={larsicomUrl} target="_blank" rel="noreferrer">
                <Button size="lg" variant="outline" className="rounded-md border-white/30 bg-transparent text-white hover:bg-white/10 h-12 px-7" data-testid="hero-secondary">
                  {t("hero_secondary", "Découvrir le LARSICOM")} <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
              {c.apk_url && (
                <a href={c.apk_url} target="_blank" rel="noreferrer" data-testid="hero-apk-btn">
                  <Button size="lg" variant="outline" className="rounded-md border-lisungi-yellow/60 bg-transparent text-lisungi-yellow hover:bg-lisungi-yellow/10 h-12 px-7">
                    <Smartphone className="mr-2 h-4 w-4" /> {t("apk_button_label", "Télécharger Lisungi AI pour Android")}
                  </Button>
                </a>
              )}
            </div>
          </div>
          <div className="relative">
            {c.cover_image ? (
              <div className="rounded-lg overflow-hidden border border-white/15 shadow-xl">
                <img src={fileUrl(c.cover_image)} alt="Espace de recherche scientifique du LARSICOM" className="w-full h-[360px] lg:h-[420px] object-cover" />
              </div>
            ) : (
              <div className="rounded-lg border border-white/15 bg-navy-800 h-[360px] lg:h-[420px] flex flex-col items-center justify-center gap-4" data-testid="cover-placeholder">
                <div className="h-16 w-16 rounded-2xl bg-white/5 flex items-center justify-center"><FlaskConical className="h-8 w-8 text-lisungi-yellow" /></div>
                <span className="font-serif text-xl text-white/85">Lisungi AI</span>
                <span className="text-white/40 text-xs font-mono uppercase tracking-[0.25em]">LARSICOM</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Bande de positionnement */}
      <div className="bg-lisungi-yellow text-navy">
        <div className="max-w-7xl mx-auto px-5 py-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center font-semibold">
          {steps.map((s, i) => (
            <span key={s + i} className="flex items-center gap-3 text-sm sm:text-base">
              {s}{i < steps.length - 1 && <ArrowRight className="h-4 w-4 opacity-50" />}
            </span>
          ))}
        </div>
      </div>

      {/* Fonctionnalités */}
      <section id="features" className="max-w-7xl mx-auto px-5 py-20 lg:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted2">{t("features_eyebrow", "Le bureau scientifique numérique")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-3">{t("features_title")}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {features.map((f) => (
            <div key={f.title} className="bg-white rounded-lg border border-[#E5E7EB] p-6 hover:shadow-md transition-shadow">
              <div className="h-11 w-11 rounded-md bg-navy flex items-center justify-center"><f.icon className="h-5 w-5 text-lisungi-yellow" /></div>
              <h3 className="font-semibold text-lg text-navy mt-4">{f.title}</h3>
              <p className="text-sm text-muted2 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LARSICOM */}
      <section id="larsicom" className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 py-20 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
          {c.larsicom_image ? (
            <div className="rounded-lg overflow-hidden border border-[#E5E7EB] shadow-md order-2 lg:order-1">
              <img src={fileUrl(c.larsicom_image)} alt="LARSICOM" className="w-full h-[400px] object-cover" />
            </div>
          ) : (
            <div className="rounded-lg overflow-hidden border border-[#E5E7EB] shadow-md bg-gradient-to-br from-navy to-navy-800 h-[400px] order-2 lg:order-1 flex flex-col items-center justify-center gap-4" data-testid="larsicom-placeholder">
              <div className="h-16 w-16 rounded-2xl bg-white/10 flex items-center justify-center"><FlaskConical className="h-8 w-8 text-lisungi-yellow" /></div>
              <span className="font-serif text-2xl text-white">LARSICOM</span>
              <span className="text-white/55 text-xs font-mono uppercase tracking-[0.2em] text-center px-8">Laboratoire de Recherches en Sciences de l'Information et de la Communication</span>
            </div>
          )}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 rounded-md bg-navy/5 px-4 py-1.5 text-xs font-mono tracking-wide text-navy mb-5">
              <FlaskConical className="h-3.5 w-3.5" /> {t("larsicom_badge", "Conçu par le LARSICOM")}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy leading-tight">{t("larsicom_title")}</h2>
            <p className="text-muted2 mt-5 leading-relaxed">{t("larsicom_intro")}</p>
            <p className="text-ink mt-4 leading-relaxed">{t("larsicom_vocation")}</p>
            <p className="text-ink mt-4 leading-relaxed">{t("larsicom_role")}</p>
            <a href={larsicomUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-navy font-semibold hover:text-lisungi-yellow transition-colors" data-testid="larsicom-website-link">
              {t("larsicom_website_label", "Visiter le site du LARSICOM")} <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Offres */}
      {pricingVisible && (
      <section id="pricing" className="max-w-7xl mx-auto px-5 py-20 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted2">{t("pricing_eyebrow", "Offres & crédits")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy mt-3">{t("pricing_title")}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {plans.map((p) => (
            <div key={p.key} className={`relative bg-white rounded-lg border p-6 flex flex-col ${p.popular ? "border-lisungi-yellow shadow-lg ring-1 ring-lisungi-yellow" : "border-[#E5E7EB]"}`} data-testid={`plan-${p.key}`}>
              {p.popular && <span className="absolute -top-3 left-6 bg-lisungi-yellow text-navy text-xs font-bold px-3 py-1 rounded-full">Populaire</span>}
              <h3 className="font-semibold text-navy text-lg">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-serif text-4xl font-bold text-navy">{p.price === null ? "Sur devis" : `${p.price}${p.currency}`}</span>
                {p.price !== null && p.price > 0 && <span className="text-sm text-muted2">{p.period}</span>}
              </div>
              <p className="text-xs font-mono text-muted2 mt-1">{p.credits ? `${p.credits} crédits / mois` : "Crédits personnalisés"}</p>
              <ul className="mt-5 space-y-2.5 flex-1">
                {p.features.map((f) => (<li key={f} className="flex items-start gap-2 text-sm text-ink"><Check className="h-4 w-4 text-lisungi-yellow mt-0.5 shrink-0" /> {f}</li>))}
              </ul>
              <Link to="/login?mode=register" className="mt-6">
                <Button className={`w-full rounded-md ${p.popular ? "bg-navy text-white hover:bg-navy-800" : "bg-white border border-navy text-navy hover:bg-navy hover:text-white"}`} data-testid={`plan-${p.key}-cta`}>
                  {p.key === "gratuit" ? "Commencer gratuitement" : p.key === "institution" ? "Nous contacter" : "Choisir cette offre"}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* NOS PARTENAIRES */}
      <section id="partners" className="bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-navy text-center">{t("partners_title", "Nos Partenaires")}</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6" data-testid="partners-grid">
            {partners.length > 0 ? partners.map((p) => (
              <div key={p.id} className="h-40 rounded-xl border border-[#E5E7EB] bg-white flex items-center justify-center p-6 hover:shadow-md transition-shadow">
                <img src={fileUrl(p.url)} alt={p.filename || "Partenaire"} className="max-h-28 max-w-full object-contain" data-testid={`partner-${p.id}`} />
              </div>
            )) : [0, 1, 2].map((i) => (
              <div key={i} className="h-40 rounded-xl border border-dashed border-[#E5E7EB] bg-lisungi-bg flex items-center justify-center">
                <span className="text-xs font-mono text-muted2">Logo partenaire</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-800 text-white/70">
        <div className="max-w-7xl mx-auto px-5 py-12 grid sm:grid-cols-3 gap-8">
          <div>
            <Logo variant="light" />
            <p className="text-sm mt-4 max-w-xs">{t("footer_tagline")}</p>
          </div>
          <div className="text-sm space-y-2">
            <p className="text-white font-semibold mb-3">{t("footer_nav_title", "Navigation")}</p>
            <a href="#larsicom" className="block hover:text-lisungi-yellow">{t("nav_larsicom", "Le LARSICOM")}</a>
            <a href="#features" className="block hover:text-lisungi-yellow">{t("nav_features", "Fonctionnalités")}</a>
            <a href="#pricing" className="block hover:text-lisungi-yellow">{t("nav_pricing", "Offres")}</a>
            <Link to="/responsable" className="block hover:text-lisungi-yellow">{t("nav_responsible", "Usage responsable")}</Link>
          </div>
          <div className="text-sm space-y-2">
            <p className="text-white font-semibold mb-3">{t("footer_access_title", "Accès")}</p>
            <Link to="/login" className="block hover:text-lisungi-yellow">{t("nav_login", "Se connecter")}</Link>
            <Link to="/login?mode=register" className="block hover:text-lisungi-yellow">Créer un compte</Link>
            <a href={larsicomUrl} target="_blank" rel="noreferrer" className="block hover:text-lisungi-yellow">Site du LARSICOM</a>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 px-5 flex items-center justify-center gap-3 text-center text-xs text-white/40 font-mono">
          <span>© {new Date().getFullYear()} {t("footer_copyright", "LARSICOM — Lisungi AI. Tous droits réservés.")}</span>
          <Link to="/login?next=/admin" title="Espace administrateur" aria-label="Administration" className="text-white/10 hover:text-lisungi-yellow transition-colors" data-testid="admin-stealth-link">
            <KeyRound className="h-3.5 w-3.5" />
          </Link>
        </div>
      </footer>
    </div>
  );
}
